import {
    Component,
    OnInit,
    Inject,
    ViewChild,
    ElementRef,
    HostListener,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
    MsalService,
    MsalBroadcastService,
    MSAL_GUARD_CONFIG,
    MsalGuardConfiguration,
} from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { AppConfigService } from './app.config';
import {
    NavEvent,
    NavItem,
} from './components/cagov/cagov-nav-megamenu/nav-megamenu.component';
import { UiService } from './shared/services/ui.service';
import { DestroyRxComponent } from './components/destroy-rx/destroy-rx.component';
import { AuthService } from './shared/services/auth.service';
import { Router } from '@angular/router';
import { AppConfig } from './shared/models/appConfig';
import {
    DocumentInterruptSource,
    Idle,
    StorageInterruptSource,
} from '@ng-idle/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TimeoutModalComponent } from './components/timeout-modal/timeout-modal.component';
import { environment } from '../environments/environment';
import { TimeoutResponse } from './shared/types/session';
import { SessionService } from './shared/services/session.service';

@Component({
    selector: 'las-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent extends DestroyRxComponent implements OnInit {
    @ViewChild('mainContent') mainContent!: ElementRef;

    @HostListener('window:scroll', [])
    onScroll() {
        this.atTop = window.scrollY === 0;
    }

    timeoutDialog?: MatDialogRef<TimeoutModalComponent>;
    idleState = IdleState.NotStarted;
    timedOut = false;

    config: AppConfig;
    appName = 'CA.gov';
    shortName: string = 'CA.gov';
    appEnvironment: string = 'Local';
    appVersion: string = '1.0.0';
    landingUrl = 'login';
    title: string;
    root = '/';

    isIframe = false;
    loginDisplay = false;
    activeAccount: any = null;
    hasError = false;

    scrollToTop = () => this.uiService.scrollToTop();
    onResize = (event: any) => this.uiService.onResize(event);
    atTop: boolean = true;

    navItems: NavItem[] = [];

    constructor(
        @Inject(MSAL_GUARD_CONFIG)
        private msalGuardConfig: MsalGuardConfiguration,
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        public uiService: UiService,
        public appConfig: AppConfigService,
        private titleService: Title,
        private lasAuth: AuthService,
        private router: Router,
        private dialog: MatDialog,
        private idle: Idle,
        sessionService: SessionService,
    ) {
        super();
        this.config = this.appConfig.getConfig();
        this.appName = this.config.title;
        this.shortName = this.config.shortName;
        this.appVersion = this.config.version;
        this.title = `${this.config.title} v${this.appVersion.split('.')[0]}`;
        this.titleService.setTitle(this.title);
        this.initializeIdleTimeout();
        // TODO: Verify this doesn't create a race condition in the auth service.
        //  It should not because the app component loads before anything else.
        // Ensures the sessions are initialized and available even when the
        // session component is not onscreen. (i.e., Log in)
        sessionService.initSession();
    }

    /**
     * Timeout interrupts to be used before an idle timeout occurs.
     */
    get interrupts() {
        return [
            new DocumentInterruptSource(environment.idleInterrupts),
            new StorageInterruptSource(),
        ];
    }

    /**
     * Interrupts to be used after an idle timeout occurs. This ensures the user makes a choice by clicking on one of
     * the modal buttons.
     */
    get timeoutInterrupts() {
        return [];
    }

    /**
     * Returns true if the user is on the login page; false, otherwise.
     */
    get atLogin() {
        return this.router.url === '/login';
    }

    /**
     * Sets the value of `isIframe` which should always be false.
     */
    ngOnInit(): void {
        // Remove this line to use Angular Universal
        this.isIframe = window !== window.parent && !window.opener;
    }

    /**
     * Handles a login/logout button click on the nav bar.
     * @param navEvent State Template navigation event.
     */
    handleNavEvent(navEvent: NavEvent): void {
        switch (navEvent.action) {
            case 'login':
                this.loginRedirect();
                break;
            case 'logout':
                this.logout();
                break;
        }
    }

    /**
     * Redirects the user to complete the MSAL login process.
     */
    loginRedirect() {
        if (this.msalGuardConfig.authRequest) {
            this.authService.loginRedirect({
                ...this.msalGuardConfig.authRequest,
            } as RedirectRequest);
        } else {
            this.authService.loginRedirect();
        }
    }

    /**
     * Initiates the MSAL logout process for the user.
     * @param popup Should a popup be displayed?
     */
    logout(popup?: boolean) {
        if (popup) {
            this.authService.logoutPopup({
                mainWindowRedirectUri: this.root,
            });
        } else {
            this.authService.logoutRedirect();
        }
    }

    /**
     * Navigates the user to the landing page which is currently /login.
     */
    navToLanding() {
        this.router.navigate([this.landingUrl]).then((result) => {
            if (!result) {
                console.error(
                    'Nav to the landing page failed or was rejected.',
                );
                // We'll block the router outlet's content and show an error.
                this.hasError = true;
            } else {
                console.log('User sent to the landing page.');
                this.hasError = false;
            }
        });
    }

    backToTop(event: MouseEvent) {
        event.preventDefault();
        this.scrollToTop();
        setTimeout(() => this.mainContent.nativeElement.focus(), 150);
    }

    private initializeIdleTimeout() {
        // Sets the timespan the user can be idle without a warning.
        this.idle.setIdle(environment.idleSeconds);
        // Sets the timespan between the warning dialog and forced logout.
        this.idle.setTimeout(environment.timeoutSeconds);
        // Sets the default interrupts.
        this.idle.setInterrupts(this.interrupts);

        this.idle.onIdleStart.subscribe(() => {
            // We don't want to show the login timeout when the user's on the login screen.
            if (this.atLogin) {
                this.reset();
                return;
            }
            this.idle.setInterrupts(this.timeoutInterrupts);
            this.idleState = IdleState.Started;
            this.displayTimeoutModal();
        });
        this.idle.onIdleEnd.subscribe(() => {
            this.idle.setInterrupts(this.interrupts);
            this.idleState = IdleState.Active;
        });
        this.idle.onTimeoutWarning.subscribe((countdown) => {
            if (this.timeoutDialog) {
                this.timeoutDialog.componentInstance.countdown = countdown;
            } else {
                // FIXME: This shouldn't ever happen, but if it does, now what?
                //  Should we log them out preemptively?
                console.error('Timeout warning without dialog instance.');
            }
        });
        this.idle.onTimeout.subscribe(() => {
            this.idleState = IdleState.TimedOut;
            this.timedOut = true;
            this.timeoutDialog?.componentInstance.onTimeout();
        });
        this.reset();
    }

    private reset() {
        this.titleService.setTitle(this.title);
        this.idle.watch();
        this.idleState = IdleState.Started;
    }

    private displayTimeoutModal() {
        this.titleService.setTitle('Timeout Warning!');
        this.timeoutDialog = this.dialog.open(TimeoutModalComponent, {
            width: '25%',
            disableClose: true,
            data: this.idle.getTimeout(),
            ariaLabel: 'timeout warning',
        });

        this.timeoutDialog.afterClosed().subscribe((result) => {
            switch (result) {
                case TimeoutResponse.Stay:
                    this.reset();
                    break;
                case TimeoutResponse.Logout:
                    this.titleService.setTitle(this.title);
                    this.lasAuth.logout();
                    break;
                default:
                    // FIXME: This shouldn't ever happen, but if it does, now what?
                    //  Should we log them out preemptively?
                    console.error('Unexpected timeout modal result!');
            }
        });
    }
}

/**
 * Representation of the various states the idle timeout can be in.
 */
enum IdleState {
    Active = 'No longer idle.',
    NotStarted = 'Not started.',
    Started = 'Started.',
    TimedOut = 'Timed out!',
}
